import {
  AlreadyRegisteredException,
  AuthenticationException,
  AuthenticationFailedException,
  ClientValidationException,
  EmailAliasException,
  InvalidTokenException,
  InvalidVerifyCodeException,
  LegacyNextAuthException,
  OAuthProviderException,
  ProviderMismatchException,
  SignInFailedException,
  ThatchAuthenticationException,
  ThatchEmailMismatchException,
  ThatchVerificationFailedException,
  VerifyCodeAlreadyRedeemedException,
  VerifyCodeExpiredException,
  VerifyCodeNotFoundException,
  VerifyCodeNotRedeemedException,
  VerifyCodeSignInExpiredException,
} from "@/types/client-exceptions"
import capitalize from "lodash/capitalize"

// Shared error message constants
const ERROR_MESSAGES = {
  AUTHENTICATION_FAILED_RETRY: "Authentication failed. Please try again.",
  THATCH_EMAIL_MISMATCH:
    "The email used for sign up does not match the Thatch Claim Account email.",
  THATCH_VERIFICATION_FAILED:
    "The link from your Thatch Claim Account email is invalid. Please check the link and try again.",
  INCORRECT_PASSWORD:
    "Incorrect password. Please check your password and try again.",
  ALREADY_REGISTERED_SIGN_IN:
    "This email is already registered. Please sign in instead.",
  EMAIL_ALIAS_NOT_SUPPORTED:
    "Email aliasing (with +alias) is not supported. Please use your main email address without any aliases.",
  INVALID_VERIFICATION_CODE:
    "Invalid verification code. Please check your email for the correct code.",
  VERIFICATION_CODE_ALREADY_USED:
    "This verification code has already been used. Please request a new one.",
  PASSWORD_RESET_LINK_INVALID:
    "This password reset link is invalid or has expired. Please request a new one.",

  // Provider mismatch pattern (with template)
  PROVIDER_MISMATCH_SIGN_IN: (providerList: string) =>
    `This email is registered with a different provider. Please sign in with ${providerList} or use a different email.`,

  // OAuth context-specific messages
  OAUTH_FAILED_SIGN_IN:
    "Authentication failed. Please try again or use a different sign-in method.",
  OAUTH_FAILED_REGISTRATION:
    "Authentication failed. Please try again or use a different registration method.",
  OAUTH_FAILED_GENERAL:
    "Authentication failed. Please try again or use a different method.",
} as const

// Simple helper function for NextAuth MtError format
function toMtError(message: string): string {
  return `[MtError]${message}`
}

export const signInErrorMessages = (errorCode: string) => {
  if (errorCode.startsWith("[MtError]")) {
    // This is a custom error code from the MtError class
    // We can return the message directly
    return errorCode.replace("[MtError]", "").trim()
  }

  errorCode = errorCode.toLowerCase()

  switch (errorCode) {
    case "signin":
    case "oauthsignin":
    case "oauthcallback":
    case "callback":
    case "oauthaccountnotlinked":
    case "credentialssignin":
    case "oauthcreateaccount":
    case "emailcreateaccount":
      return "Invalid credentials. Make sure you typed your email and password correctly and try again."
    // TODO: use this instead once we have a separate error message for the email
    // return "Incorrect password. Make sure you typed it correctly and try again."
    case "emailsignin":
      return "The e-mail could not be sent."
    case "sessionrequired":
      return "Please log in to access this page."
    case "auth_loop_detected":
      return "We detected an authentication issue. Please clear your browser cookies and try again. If the problem persists, contact support."
    default:
      return "Unable to log in."
  }
}

export function signUpErrorMessages(errorCode: string) {
  errorCode = errorCode.toLowerCase()
  if (errorCode.startsWith("[MtError]")) {
    // This is a custom error code from the MtError class
    // We can return the message directly
    return errorCode.replace("[MtError]", "").trim()
  }

  switch (errorCode) {
    case "oauthaccountnotlinked":
    case "accountnotlinkederror":
      return "This email is already registered. Please log in with the same method you originally used to sign up."
    default:
      return "Unable to register."
  }
}

export function getEmailStepErrorMessage(error: unknown): string {
  if (error instanceof LegacyNextAuthException) {
    return error.message
  }

  return "Unable to continue. Please try again."
}

export function getSignInStepErrorMessage(error: unknown): string {
  if (error instanceof LegacyNextAuthException) {
    return error.message
  }

  if (error instanceof SignInFailedException) {
    return ERROR_MESSAGES.INCORRECT_PASSWORD
  }

  if (error instanceof OAuthProviderException) {
    return ERROR_MESSAGES.OAUTH_FAILED_SIGN_IN
  }

  if (error instanceof ThatchVerificationFailedException) {
    return ERROR_MESSAGES.THATCH_VERIFICATION_FAILED
  }

  if (error instanceof ThatchAuthenticationException) {
    return ERROR_MESSAGES.AUTHENTICATION_FAILED_RETRY
  }

  if (error instanceof ThatchEmailMismatchException) {
    return ERROR_MESSAGES.THATCH_EMAIL_MISMATCH
  }

  if (error instanceof ProviderMismatchException) {
    const providerList = mapMismatchProvider(error)
    if (providerList.length > 0) {
      return ERROR_MESSAGES.PROVIDER_MISMATCH_SIGN_IN(providerList)
    }
  }

  return "Unable to log in. Please check your credentials and try again."
}

export function getRegisterStepErrorMessage(error: unknown): string {
  if (error instanceof AlreadyRegisteredException) {
    return ERROR_MESSAGES.ALREADY_REGISTERED_SIGN_IN
  }

  if (error instanceof EmailAliasException) {
    return ERROR_MESSAGES.EMAIL_ALIAS_NOT_SUPPORTED
  }

  if (error instanceof OAuthProviderException) {
    return ERROR_MESSAGES.OAUTH_FAILED_REGISTRATION
  }

  if (error instanceof ThatchVerificationFailedException) {
    return ERROR_MESSAGES.THATCH_VERIFICATION_FAILED
  }

  if (error instanceof ThatchAuthenticationException) {
    return ERROR_MESSAGES.AUTHENTICATION_FAILED_RETRY
  }

  if (error instanceof ThatchEmailMismatchException) {
    return ERROR_MESSAGES.THATCH_EMAIL_MISMATCH
  }

  if (error instanceof ProviderMismatchException) {
    const providerList = mapMismatchProvider(error)
    if (providerList.length > 0) {
      return ERROR_MESSAGES.PROVIDER_MISMATCH_SIGN_IN(providerList)
    }
  }

  return "Unable to register. Please check your information and try again."
}

export function getResetPasswordErrorMessage(error: unknown): string {
  if (error instanceof LegacyNextAuthException) {
    return error.message
  }

  if (error instanceof InvalidTokenException) {
    return ERROR_MESSAGES.PASSWORD_RESET_LINK_INVALID
  }

  if (error instanceof AuthenticationException) {
    return ERROR_MESSAGES.PASSWORD_RESET_LINK_INVALID
  }

  if (error instanceof VerifyCodeExpiredException) {
    return "This password reset code has expired. Please request a new one."
  }

  if (
    error instanceof VerifyCodeNotFoundException ||
    error instanceof InvalidVerifyCodeException
  ) {
    return "This password reset code is invalid. Please request a new one."
  }

  return "An error occurred, could not reset password at this time."
}

export function getEmailVerifyErrorMessage(error: Error): string {
  if (error instanceof LegacyNextAuthException) {
    return error.message
  }

  if (error instanceof InvalidVerifyCodeException) {
    return ERROR_MESSAGES.INVALID_VERIFICATION_CODE
  }

  if (error instanceof VerifyCodeAlreadyRedeemedException) {
    return ERROR_MESSAGES.VERIFICATION_CODE_ALREADY_USED
  }

  return "An error occurred, could not verify code at this time."
}

// we can't determine register vs sign in,  in our next-auth flow so need to handle both cases
export function getNextAuthErrorMessage(error: unknown): string | undefined {
  if (error instanceof AuthenticationFailedException) {
    return toMtError(ERROR_MESSAGES.AUTHENTICATION_FAILED_RETRY)
  }

  if (error instanceof SignInFailedException) {
    return toMtError(ERROR_MESSAGES.INCORRECT_PASSWORD)
  }

  if (error instanceof AlreadyRegisteredException) {
    return toMtError(ERROR_MESSAGES.ALREADY_REGISTERED_SIGN_IN)
  }

  if (error instanceof EmailAliasException) {
    return toMtError(ERROR_MESSAGES.EMAIL_ALIAS_NOT_SUPPORTED)
  }

  if (error instanceof InvalidTokenException) {
    return toMtError(
      "This link is invalid or has expired. Please request a new one."
    )
  }

  if (error instanceof OAuthProviderException) {
    return toMtError(ERROR_MESSAGES.OAUTH_FAILED_GENERAL)
  }

  if (error instanceof ThatchVerificationFailedException) {
    return toMtError(ERROR_MESSAGES.THATCH_VERIFICATION_FAILED)
  }

  if (error instanceof ThatchAuthenticationException) {
    return toMtError(ERROR_MESSAGES.AUTHENTICATION_FAILED_RETRY)
  }

  if (error instanceof ThatchEmailMismatchException) {
    return toMtError(ERROR_MESSAGES.THATCH_EMAIL_MISMATCH)
  }

  if (error instanceof ProviderMismatchException) {
    const providerList = mapMismatchProvider(error)
    if (providerList.length > 0) {
      return toMtError(ERROR_MESSAGES.PROVIDER_MISMATCH_SIGN_IN(providerList))
    }
  }

  if (error instanceof VerifyCodeExpiredException) {
    return toMtError("Verification code expired. Please request a new one.")
  }

  if (error instanceof VerifyCodeAlreadyRedeemedException) {
    return toMtError(ERROR_MESSAGES.VERIFICATION_CODE_ALREADY_USED)
  }

  if (error instanceof VerifyCodeNotRedeemedException) {
    return toMtError("Please verify your email first before signing in.")
  }

  if (
    error instanceof VerifyCodeSignInExpiredException ||
    error instanceof VerifyCodeNotFoundException
  ) {
    return toMtError("Sign-in verification has expired. Please start over.")
  }

  if (error instanceof InvalidVerifyCodeException) {
    return toMtError(ERROR_MESSAGES.INVALID_VERIFICATION_CODE)
  }

  // no fallback here, this is handled by next-auth
  return undefined
}

function mapMismatchProvider(error: ProviderMismatchException) {
  const providers = error.data?.valid_providers || []
  return new Intl.ListFormat("en", {
    type: "disjunction",
  }).format(
    providers.map((p) => {
      if (p === "email") {
        return "a password"
      }
      return capitalize(p)
    })
  )
}

export function getEmailChangeErrorMessage(error: unknown): string {
  if (error instanceof SignInFailedException) {
    return ERROR_MESSAGES.INCORRECT_PASSWORD
  }

  if (error instanceof ClientValidationException) {
    return error.message
  }

  return "Unable to change email. Please try again."
}
